import { forwardRef, useEffect, useRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { classNames } from "../internal/classNames";
import "./ChoiceControl.css";

export type ChoiceControlVariant = "checkbox" | "radio" | "chip";

export interface ChoiceControlProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "children" | "type"> {
  variant?: ChoiceControlVariant;
  label: ReactNode;
  description?: ReactNode;
  error?: string;
  indeterminate?: boolean;
}

export const ChoiceControl = forwardRef<HTMLInputElement, ChoiceControlProps>(function ChoiceControl(
  {
    variant = "checkbox",
    label,
    description,
    error,
    indeterminate = false,
    className,
    id,
    disabled,
    ...props
  },
  forwardedRef,
) {
  const localRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (localRef.current) localRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const setRef = (element: HTMLInputElement | null) => {
    localRef.current = element;
    if (typeof forwardedRef === "function") forwardedRef(element);
    else if (forwardedRef) forwardedRef.current = element;
  };

  return (
    <label className={classNames("cp-choice", `cp-choice--${variant}`, className)} data-error={Boolean(error) || undefined}>
      <input
        {...props}
        ref={setRef}
        id={id}
        type={variant === "radio" ? "radio" : "checkbox"}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        className="cp-choice__native"
      />
      <span aria-hidden="true" className="cp-choice__indicator">
        <svg className="cp-choice__check" fill="none" viewBox="0 0 16 16">
          <path d={indeterminate ? "M4 8h8" : "m3.5 8 3 3 6-6"} />
        </svg>
      </span>
      <span className="cp-choice__content">
        <span className="cp-choice__label">
          {variant === "chip" && (
            <svg aria-hidden="true" className="cp-choice__chip-check" fill="none" viewBox="0 0 16 16">
              <path d="m3.5 8 3 3 6-6" />
            </svg>
          )}
          {label}
        </span>
        {description && <span className="cp-choice__description">{description}</span>}
        {error && <span className="cp-choice__error" role="alert">{error}</span>}
      </span>
    </label>
  );
});
